import Vue from 'vue';
import Component from 'vue-class-component';
import { Action, Getter, Mutation } from 'vuex-class';
import { Prop } from 'vue-property-decorator';
import { StoreNamespace } from '@/globals/StoreNamespaceGlobal';
import MockData from '@/globals/mock';
import { Grid } from '@/models/Grid.model';
import { Utils } from '@/globals/Utils';
import { GridColumn } from '@/models/GridColumn.model';
import { ClickColumn } from '@/models/ClickColumn.model';
import { InsurersReportsSearchModel } from '@/models/backoffice/InsurersReportsSearchModel';
import { InsurersReportModel } from '@/models/backoffice/InsurersReportModel';
import { InsurersReportSelectItemModel } from '@/models/backoffice/InsurersReportSelectItemModel';
import {
  GET_INSURERS_REPORTS,
  INSURERS_REPORTS_LIST,
  INSURERS_REPORTS_ERRORS,
  INSURERS_REPORTS_LOADING,
  SET_INSURERS_REPORTS,
  SET_INSURERS_REPORTS_ERRORS,
  SET_INSURERS_REPORTS_LOADING,
  DOWNLOAD_INSURERS_REPORTS,
} from '@/types/insurers-reports.types';

@Component({})
export default class InsurersReportsComponent extends Vue {

  @Action(GET_INSURERS_REPORTS, StoreNamespace.INSURERS_REPORTS) public getInsurersReports;
  @Action(DOWNLOAD_INSURERS_REPORTS, StoreNamespace.INSURERS_REPORTS) public downloadInsurersReports;

  @Mutation(SET_INSURERS_REPORTS, StoreNamespace.INSURERS_REPORTS) public setInsurersReports;
  @Mutation(SET_INSURERS_REPORTS_ERRORS, StoreNamespace.INSURERS_REPORTS) public setInsurersReportsErrors;
  @Mutation(SET_INSURERS_REPORTS_LOADING, StoreNamespace.INSURERS_REPORTS) public setInsurersReportsLoading;

  @Getter(INSURERS_REPORTS_LIST, StoreNamespace.INSURERS_REPORTS) public insurersReportsList!: Grid<InsurersReportModel>;
  @Getter(INSURERS_REPORTS_ERRORS, StoreNamespace.INSURERS_REPORTS) public insurersReportsErrors!: string[];
  @Getter(INSURERS_REPORTS_LOADING, StoreNamespace.INSURERS_REPORTS) public insurersReportsLoading!: boolean;

  @Prop(String) public triggerSearch!: string;

  private insurersReportsColumns: GridColumn[] = [];
  private cleared = false;
  private clientOptions: InsurersReportSelectItemModel[] = MockData.insurersReports.clientOptions;
  private statusOptions: InsurersReportSelectItemModel[] = MockData.insurersReports.statusOptions;

  private static getEmptyInsurersReportsSearchObject(): InsurersReportsSearchModel {
    return {
      indexNum: null,
      status: undefined,
      dateToFetch: null,
      pageNumber: 0,
      pageSize: 10,
    };
  }

  public insurersReportsSearchObject: InsurersReportsSearchModel = InsurersReportsComponent.getEmptyInsurersReportsSearchObject();

  public created() {
    this.initInsurersReportsColumns();
  }

  public mounted() {
    this.setInsurersReportsLoading(false);
    this.setInsurersReportsErrors([]);
    if (this.triggerSearch) {
      setTimeout(() => {
        this.$emit('fetchSearch', this.insurersReportsSearchObject);
      }, 100);
    }
  }

  private initInsurersReportsColumns() {
    this.insurersReportsColumns.push({
      property: 'startDate',
      header: 'back_office.insurers_reports.start_date',
      hideSorting: true,
    } as GridColumn);
    this.insurersReportsColumns.push({
      property: 'endDate',
      header: 'back_office.insurers_reports.end_date',
      hideSorting: true,
    } as GridColumn);
    this.insurersReportsColumns.push({
      property: 'status',
      header: 'back_office.insurers_reports.status',
      hideSorting: true,
    } as GridColumn);
    this.insurersReportsColumns.push({
      property: 'rejectFormsCount',
      header: 'back_office.insurers_reports.rejectFormsCount',
      hideSorting: true,
    } as GridColumn);
    this.insurersReportsColumns.push({
      property: 'approvedFormsCount',
      header: 'back_office.insurers_reports.approvedFormsCount',
      hideSorting: true,
    } as GridColumn);
    this.insurersReportsColumns.push({
      property: 'transferErrorMsg',
      header: 'back_office.insurers_reports.transferErrorMsg',
      hideSorting: true,
    } as GridColumn);
    this.insurersReportsColumns.push({
      property: 'transferStatus',
      header: 'back_office.insurers_reports.transferStatus',
      hideSorting: true,
    } as GridColumn);
    this.insurersReportsColumns.push({
      property: 'transferStatusDate',
      header: 'back_office.insurers_reports.transferStatusDate',
      hideSorting: true,
    } as GridColumn);
    this.insurersReportsColumns.push({
      clickEvent: 'DOWNLOAD_FILE',
      faIcon: 'download',
      faIconType: 'fal',
      header: 'back_office.insurers_reports.download',
      hideSorting: true,
    } as ClickColumn);
  }

  public updateDate(date: Date) {
    this.insurersReportsSearchObject.dateToFetch = date ? Utils.dateToString(date) : date;
  }

  private onSearch() {
    this.setInsurersReportsLoading(true);
    this.setInsurersReportsErrors([]);
    this.getInsurersReports(this.insurersReportsSearchObject);
  }

  private onClear() {
    this.cleared = true;
    this.setInsurersReportsErrors([]);
    this.setInsurersReports({} as Grid);
    this.insurersReportsSearchObject = InsurersReportsComponent.getEmptyInsurersReportsSearchObject();
    setTimeout(() => {
      this.cleared = false;
    });
  }

  private preparedFilename(fileId: string): string {
    return `report_mivtachim__client:${this.insurersReportsSearchObject.indexNum}_date:${this.insurersReportsSearchObject.dateToFetch}_id:${fileId}.csv`;
  }

  public onTdClicked(item, column) {
    switch (column.clickEvent) {
      case 'DOWNLOAD_FILE':
        this.downloadInsurersReports({ fileId: item.id, fileName: this.preparedFilename(item.id) });
        break;
    }
  }

  public onRecordsPerPageChanged(records: number) {
    this.insurersReportsSearchObject.pageSize = records;
    this.getInsurersReports(this.insurersReportsSearchObject);
  }

  public onPageChange(page: number) {
    this.insurersReportsSearchObject.pageNumber = page;
    this.getInsurersReports(this.insurersReportsSearchObject);
  }

  private beforeDestroy() {
    this.setInsurersReportsErrors([]);
    this.setInsurersReports({} as Grid);
    this.insurersReportsSearchObject = InsurersReportsComponent.getEmptyInsurersReportsSearchObject();
  }
}

